import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import LandlordSidebar from '../../components/landlord/LandlordSidebar';
import LegalFooter from '../../components/shared/LegalFooter';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMonth = (m) => { const [y, mo] = m.split('-'); return new Date(y, mo - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); };

const CELL = { padding: '10px 14px', textAlign: 'right', fontSize: 13, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
const HEAD = { padding: '10px 14px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', whiteSpace: 'nowrap' };

export default function LandlordReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState('6');
  const [propertyId, setPropertyId] = useState('');
  const [expandedProps, setExpandedProps] = useState({});

  const loadReport = (overrides = {}) => {
    const m = overrides.months || months;
    const p = overrides.propertyId !== undefined ? overrides.propertyId : propertyId;
    setLoading(true);
    let url = '/reports/landlord?months=' + m;
    if (p) url += '&property_id=' + p;
    api.get(url)
      .then(res => { setData(res.data); setExpandedProps({}); })
      .catch(() => toast.error('Could not load report'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadReport(); }, []);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Property', 'Month', 'Total Units', 'Active Tenants', 'Penetration %', 'Revenue']];
    data.properties.forEach(p => {
      p.monthly.forEach(m => {
        rows.push([p.name, m.month, m.total_units, m.active_tenants, m.penetration_pct + '%', m.gross_revenue]);
      });
    });
    const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'property_report_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  const toggleProp = (id) => setExpandedProps(p => ({ ...p, [id]: !p[id] }));

  const properties = data?.properties || [];
  const allProperties = properties;

  return (
    <div className="app-layout">
      <Topbar role="landlord" />
      <div className="dashboard-layout">
        <LandlordSidebar />
        <div style={{ flex: 1, padding: '2rem', maxWidth: 1100, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>Reports</h1>
              <p style={{ color: '#64748b', fontSize: 14 }}>Month-by-month performance for your properties.</p>
            </div>
            <button onClick={exportCSV} disabled={!data} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: !data ? 0.5 : 1 }}>
              ⬇ Export CSV
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, margin: '16px 0 20px', flexWrap: 'wrap' }}>
            <select value={months} onChange={e => { setMonths(e.target.value); loadReport({ months: e.target.value }); }}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white' }}>
              {[3,6,12,24].map(m => <option key={m} value={m}>{m} Months</option>)}
            </select>
            <select value={propertyId} onChange={e => { setPropertyId(e.target.value); loadReport({ propertyId: e.target.value }); }}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, background: 'white', minWidth: 180 }}>
              <option value="">All Properties</option>
              {allProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Generating report...</div>
          ) : !data || !properties.length ? (
            <div style={{ background: 'white', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#94a3b8', border: '1px solid #e2e8f0' }}>
              No data available yet.
            </div>
          ) : (
            properties.map(prop => (
              <div key={prop.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 20, overflow: 'hidden' }}>
                <div onClick={() => toggleProp(prop.id)}
                  style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: expandedProps[prop.id] ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{prop.name}</span>
                    <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 12 }}>{prop.address}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{prop.unit_count}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Units</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>{prop.monthly[prop.monthly.length - 1]?.active_tenants || 0}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Active</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#2563eb' }}>{prop.monthly[prop.monthly.length - 1]?.penetration_pct || 0}%</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Penetration</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#16a34a' }}>${fmt(prop.monthly[prop.monthly.length - 1]?.gross_revenue || 0)}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Rev MTD</div>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: 18 }}>{expandedProps[prop.id] ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expandedProps[prop.id] && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ ...HEAD, textAlign: 'left' }}>Month</th>
                          <th style={HEAD}>Total Units</th>
                          <th style={HEAD}>Active Tenants</th>
                          <th style={HEAD}>Penetration</th>
                          <th style={HEAD}>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prop.monthly.map((m, i) => (
                          <tr key={m.month} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ ...CELL, textAlign: 'left', fontWeight: 600 }}>{fmtMonth(m.month)}</td>
                            <td style={CELL}>{m.total_units}</td>
                            <td style={{ ...CELL, color: '#16a34a', fontWeight: 600 }}>{m.active_tenants}</td>
                            <td style={{ ...CELL, fontWeight: 600, color: m.penetration_pct >= 70 ? '#16a34a' : m.penetration_pct >= 40 ? '#f59e0b' : '#dc2626' }}>
                              {m.penetration_pct}%
                            </td>
                            <td style={{ ...CELL, color: '#16a34a', fontWeight: 600 }}>{m.gross_revenue > 0 ? '$' + fmt(m.gross_revenue) : '—'}</td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f0f9ff', fontWeight: 700 }}>
                          <td style={{ ...CELL, textAlign: 'left', color: '#2563eb' }}>TOTAL</td>
                          <td style={CELL}>—</td>
                          <td style={CELL}>—</td>
                          <td style={CELL}>—</td>
                          <td style={{ ...CELL, color: '#16a34a' }}>${fmt(prop.monthly.reduce((s, m) => s + (m.gross_revenue || 0), 0))}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}
